import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * The editor's root element, and focus moves into it by aria label.
 *
 * The element is kept after React detaches the ref: hiding an Activity pane detaches refs
 * but leaves the element in the document, and an unmount removes it. That is how work
 * finishing later tells a hidden editor from one that is gone.
 */
export function useEditorRoot() {
  const rootNodeRef = useRef<HTMLDivElement | null>(null);
  const setRootNode = useCallback((node: HTMLDivElement | null) => {
    if (node) rootNodeRef.current = node;
  }, []);
  const getIsEditorMounted = useCallback(() => Boolean(rootNodeRef.current?.isConnected), []);
  // Set to the aria label of a field rendered later, focused once it is in the document.
  const [focusTarget, setFocusTarget] = useState<string | null>(null);

  useEffect(() => {
    if (!focusTarget) return;
    const input = rootNodeRef.current?.querySelector(`[aria-label="${focusTarget}"]`);
    if (!(input instanceof HTMLElement)) return;

    input.focus();
    setFocusTarget(null);
  }, [focusTarget]);

  return { focusField: setFocusTarget, getIsEditorMounted, setRootNode };
}
